import {
    Box, Button, IconButton, Stack, Tab, Typography,
    LinearProgress, Menu, MenuItem,
    InputAdornment, OutlinedInput,
    Grid, FormControl, InputLabel, Breadcrumbs, Select, Divider,
    Alert,
} from '@mui/material';
import { ChangeEvent, useState, useEffect, useMemo } from "react";
import { Form, Formik, useFormik } from 'formik';
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { MFSubmitButton } from '../../../lib/formik';
import { values } from 'lodash';
import { validationSchemaPortfolioSummary } from '../../../utils/schema';
import { RmDashboardType, DocumentViewResponse } from '../../../redux-store/types/api-types';
import { DocumentBodyType, documentViewData } from '../../../redux-store/types/api-types';
import { ColumnType, DataTable } from '../../DataTable';
import { getDocumentViewList, getDocumentDownload } from '../../../redux-store/actions/userManagement';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import SearchIcon from '@mui/icons-material/Search';
import { DownloadsIcon } from '../../customSVGs';
import { useDebounce } from '../../../utils/useDebounce';
import { RootStateType } from '../../../redux-store/reducers';
import { downloadMultipleFiles, downloadFile } from '../../../utils/utilityFunctions';
const headers = (): ColumnType[] => {
    return [
        {
            header: 'Document Name',
            sortingKey: 'DocumentName',
            valueGetter: (row: documentViewData) => row.DocumentName || 'N/A',
        },
        {
            header: 'Description',
            sortingKey: 'Description',
            valueGetter: (row: documentViewData) => row.Description || 'N/A',
        },
        {
            header: 'Report Date',
            sortingKey: 'Input_DateTime',
            valueGetter: (row: documentViewData) => row.Input_DateTime?.split('T')[0] || 'N/A',
        },

        {
            header: 'Document Validity',
            sortingKey: 'ValidFromDt',
            valueGetter: (row: documentViewData) => (row.ValidFromDt + "-" + row.ValidToDt) || 'N/A',
        },
        {
            header: '\xa0',
            renderEditDeleteCell: (row: documentViewData): JSX.Element => {
                const dispatch = useDispatch();
                const history = useHistory();
                const [isDisabled, setIsDisabled] = useState<boolean>(false);
                const [downloadAlert, setDownloadAlert] = useState<boolean>(false);

                const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
                    setIsDisabled(true);

                    const response = (await dispatch(
                        getDocumentDownload(row.DocumentName)
                    )) as unknown as any;

                    if (response.ResponseCode != 100) {
                        setDownloadAlert(true);
                        setIsDisabled(false);
                    }
                    if (response.ResponseCode == 100) {
                        downloadFile(response.documentData.data, "", response.fileType, response.filename)
                        setIsDisabled(false);
                    }


                };

                return (
                    <>
                        <Button
                            sx={{
                                fontSize: 14, fontWeight: 500, p: 1,
                                color: '#2057A6', borderColor: '#2057A6',
                                '&:hover': {
                                    borderColor: '#2057A6',       // Even darker border on hover
                                    backgroundColor: 'rgba(16, 57, 116, 0.1)', // Slight hover background if you want
                                },
                            }}
                            variant="outlined"
                            id={`actions-btn-${row.ID}`} // Dynamic ID based on row.ID
                            onClick={handleClick}
                            disabled={isDisabled}
                        >
                            <DownloadsIcon />
                            Download
                        </Button>
                        {
                            downloadAlert &&

                            <Alert sx={{ width: { xs: '120%', sm: 'auto' } }} severity="error" onClose={() => { setDownloadAlert(false); }}>File not found</Alert>
                        }
                    </>
                );
            },
        },
    ];
};
export default function DocumentViewTabPanel(): JSX.Element {

    const currentDate = dayjs();
    const firstDayOfMonth = currentDate.startOf('month');
    const [docData, setDocData] = useState<DocumentViewResponse | null>(null);
    const [isLoading, setLoading] = useState(false);
    const [search, setSearch] = useState<string>('');
    const debounceSearchString = useDebounce(search, 500);
    const dispatch = useDispatch();

    const {
        page,
        search: storeSearch,
    } = useSelector((store: RootStateType) => store.paramsObj);
    const [noOfPages, setNoPages] = useState(1);
    const [currentPage, setPage] = useState(page);



    useEffect(() => {
        async function fetchData() {
            try {
                setLoading(true);
                const response = (await dispatch(
                    getDocumentViewList({
                        PageNumber: currentPage,
                        PageSize: "25",
                        SortBy: "desc",
                        SortByColumnName: "ID",
                        ID: "0",
                        Record_Type: "L",
                        Company_Code: "GLOBAL",
                        SearchVal: debounceSearchString,  // Use debounced string
                    })
                )) as unknown as DocumentViewResponse;

                setDocData(response);

                const recordsPerPage = 25;
                const totalRecords = response.documentViewData?.length ?? 0;
                const numberOfPages = Math.ceil(totalRecords / recordsPerPage);
                setNoPages(numberOfPages);
                setLoading(false);

            } catch (error) {
                // console.error("Failed to fetch document view list:", error);
            }
        }
        fetchData();
    }, [debounceSearchString, currentPage]);

    const filteredData = docData?.documentViewData ?? [];

    const handlePageChange = (event: ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    return (
        <Box
            sx={{
                bgcolor: 'common.white',
                boxShadow: '0px 4px 28px 2px rgba(0, 0, 0, 0.08)',
                borderRadius: '5px',
                // width:"fit-content",
            }}>
            <Box
                sx={{
                    display: { xs: 'block', sm: 'flex' },
                    alignItems: 'left',
                    justifyContent: 'end',
                    pb: 1,
                }}>
                <Box
                    sx={{ display: 'flex', alignItems: 'center', mt: { xs: 2, sm: 0 }, pt: 3, pl: 3 }}>
                    <FormControl sx={{ width: 250, mr: 2 }} variant="outlined">
                        <OutlinedInput
                            value={search}
                            onChange={({ target: { value } }) => {
                                setSearch(value);
                            }}
                            sx={{ '& .MuiOutlinedInput-input': { py: 1, fontSize: 13 } }}
                            id="search-field"
                            placeholder="Search"
                            startAdornment={
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            }
                        />
                    </FormControl>
                </Box>

            </Box>

            <Box
                mt={2.5}
                sx={{ maxWidth: { lg: '100%' }, maxHeight: { md: '55vh', lg: '67vh' } }}>
                {docData && (
                    <>
                        <DataTable
                            isLoading={isLoading}
                            tableHeader={[...headers()]}
                            tableData={filteredData}
                            rowCustomStyles={{
                                '&:last-child td, &:last-child th': { border: 0, },
                                '.MuiTableCell-body ': { color: '#686e72' },
                                background: 'transparent',
                            }}
                            boxStyles={{
                                minWidth: 500,
                            }}
                            noOfPages={noOfPages}
                            currentPage={currentPage}
                            paginationAction={handlePageChange}

                        />
                    </>
                )}
            </Box>

        </Box>
    );
}
